import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Text, Group, Badge, Paper, Table, Stack, Loader, Button, Tabs, Progress, Tooltip, ActionIcon, TextInput, Checkbox, Select } from '@mantine/core';
import { ShoppingCart, Package, TrendUp, TrendDown, Warning, Sparkle, MagnifyingGlass, Download, CaretDown, CaretUp, WarningCircle, CurrencyCircleDollar, CheckCircle, ArrowRight } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import api from '../utils/api';
import { formatMoney, currencySymbol } from '../utils/money';

function StatCard({ label, value, sub, color }) {
  return (
    <Paper withBorder p="sm" radius="md">
      <Text size="xs" c="dimmed" mb={2}>{label}</Text>
      <Text size="lg" fw={700} c={color}>{value}</Text>
      {sub && <Text size="xs" c="dimmed" mt={2}>{sub}</Text>}
    </Paper>
  );
}

export default function ShoppingDashboard({ campaignId, currency = 'PLN' }) {
  const { t } = useTranslation();
  const fmt = (v) => formatMoney(v, currency);
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [tab, setTab] = useState(() => new URLSearchParams(window.location.search).get('shoptab') || 'products');

  // Keep the active Shopping sub-tab in the URL (?shoptab=...) so it is
  // bookmarkable and survives reload, alongside the page's ?tab=shopping.
  const changeTab = (value) => {
    setTab(value);
    const params = new URLSearchParams(window.location.search);
    if (value && value !== 'products') params.set('shoptab', value);
    else params.delete('shoptab');
    window.history.replaceState({}, '', `${window.location.pathname}?${params}`);
  };
  const [search, setSearch] = useState('');
  const [sortField, setSortField] = useState('total_cost');
  const [sortDir, setSortDir] = useState('desc');
  const [analyzing, setAnalyzing] = useState(false);
  const [analysis, setAnalysis] = useState(null);
  const [analyzeProgress, setAnalyzeProgress] = useState(null);
  const analysisPollRef = useRef(null);
  const [imageAnalysis, setImageAnalysis] = useState(null);
  const [analyzingImages, setAnalyzingImages] = useState(false);
  // Bids
  const [bids, setBids] = useState(null);
  const [bidsLoading, setBidsLoading] = useState(false);
  const [selectedBids, setSelectedBids] = useState(new Set());
  const [applyingBids, setApplyingBids] = useState(false);
  const [bidResult, setBidResult] = useState(null);
  // Full product list
  const [allProducts, setAllProducts] = useState(null);
  const [allProductsPage, setAllProductsPage] = useState(1);
  const [allProductsTotal, setAllProductsTotal] = useState(0);
  const [allProductsLoading, setAllProductsLoading] = useState(false);
  const [serverSearch, setServerSearch] = useState('');

  const [titleImpact, setTitleImpact] = useState(null);
  const [markingApplied, setMarkingApplied] = useState(false);

  const loadTitleImpact = useCallback(() => {
    api.get(`/ads/campaigns/${campaignId}/shopping/titles/`)
      .then(res => setTitleImpact(res.data))
      .catch(() => {});
  }, [campaignId]);

  useEffect(() => {
    api.get(`/ads/campaigns/${campaignId}/shopping/`)
      .then(res => setData(res.data))
      .catch(() => setError(t('shopping.error.loadFailed')))
      .finally(() => setLoading(false));
    loadTitleImpact();
  }, [campaignId, loadTitleImpact]);

  const handleMarkTitlesApplied = async () => {
    setMarkingApplied(true);
    try {
      await api.post(`/ads/campaigns/${campaignId}/shopping/titles/`);
      loadTitleImpact();
    } catch (e) { /* ignore */ }
    finally { setMarkingApplied(false); }
  };

  // Deep-link support: when arriving directly on the bids sub-tab (?shoptab=bids),
  // load its data since no tab click fires on reload.
  useEffect(() => {
    if (tab === 'bids' && !bids && data) loadBids();
    if (tab === 'all-products' && allProducts === null && data) loadAllProducts(1, '');
  }, [tab, data]);

  // Resume feed analysis across reloads: show the last result, and if a job is
  // still running (task id persisted on the campaign), reattach the progress poll.
  useEffect(() => {
    if (!data) return;
    if (data.feed_analysis_result && !analysis) setAnalysis(data.feed_analysis_result);
    if (data.feed_analysis_task_id && !analyzing && !analysisPollRef.current) {
      pollAnalysis(data.feed_analysis_task_id);
    }
  }, [data]);

  useEffect(() => () => { if (analysisPollRef.current) clearTimeout(analysisPollRef.current); }, []);

  // Poll a running feed-analysis task until it finishes. Survives reloads because
  // the task id is persisted on the campaign and re-fetched in the dashboard load.
  const pollAnalysis = useCallback((taskId) => {
    if (!taskId) return;
    setAnalyzing(true);
    const tick = async () => {
      try {
        const res = await api.get(`/ads/campaigns/${campaignId}/shopping/analyze/status/${taskId}/`);
        const d = res.data || {};
        if (d.state === 'PROGRESS') {
          setAnalyzeProgress({ current: d.current || 0, total: d.total || 0, suggestions: d.suggestions || 0 });
          analysisPollRef.current = setTimeout(tick, 2000);
        } else if (d.finished || d.state === 'SUCCESS') {
          setAnalyzeProgress(null);
          setAnalyzing(false);
          if (d.error) setError(d.error);
          else setAnalysis(d);
        } else if (d.state === 'FAILURE') {
          setAnalyzeProgress(null); setAnalyzing(false);
          setError(t('shopping.error.analysisFailed'));
        } else {
          // PENDING / STARTED: keep waiting
          analysisPollRef.current = setTimeout(tick, 2000);
        }
      } catch {
        analysisPollRef.current = setTimeout(tick, 3000);
      }
    };
    tick();
  }, [campaignId]);

  const handleAnalyze = async () => {
    setError('');
    setAnalysis(null);
    setAnalyzing(true);
    setAnalyzeProgress({ current: 0, total: 0, suggestions: 0 });
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/shopping/analyze/`);
      if (res.data?.task_id) pollAnalysis(res.data.task_id);
      else { setAnalyzing(false); setAnalyzeProgress(null); }
    } catch {
      setError(t('shopping.error.analyzeStartFailed'));
      setAnalyzing(false);
      setAnalyzeProgress(null);
    }
  };

  const handleImageAnalysis = async () => {
    if (!data?.shopping_settings?.merchant_center_id) return;
    setAnalyzingImages(true);
    try {
      const res = await api.post(`/ads/merchant-centers/${data.shopping_settings.merchant_center_id}/image-analysis/`, { max_products: 20 });
      setImageAnalysis(res.data);
    } catch {}
    setAnalyzingImages(false);
  };

  const loadBids = async () => {
    setBidsLoading(true);
    try {
      const res = await api.get(`/ads/campaigns/${campaignId}/shopping/bids/`);
      setBids(res.data);
      setSelectedBids(new Set(res.data.bid_recommendations.map(r => r.product_group_id)));
    } catch {}
    setBidsLoading(false);
  };

  const handleApplyBids = async () => {
    if (selectedBids.size === 0) return;
    setApplyingBids(true);
    try {
      const res = await api.post(`/ads/campaigns/${campaignId}/shopping/bids/apply/`, {
        recommendation_ids: [...selectedBids],
      });
      setBidResult(res.data);
    } catch {}
    setApplyingBids(false);
  };

  const loadAllProducts = useCallback(async (page = 1, searchQuery = '') => {
    setAllProductsLoading(true);
    try {
      const params = new URLSearchParams({ page, per_page: 50 });
      if (searchQuery) params.set('search', searchQuery);
      const res = await api.get(`/ads/campaigns/${campaignId}/shopping/products/?${params}`);
      setAllProducts(res.data.products);
      setAllProductsTotal(res.data.total);
      setAllProductsPage(page);
    } catch {}
    setAllProductsLoading(false);
  }, [campaignId]);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDir('desc');
    }
  };

  if (loading) return <div className="flex justify-center py-12"><Loader color="brand" /></div>;
  if (error) return <Paper withBorder p="lg" radius="md"><Text c="red">{error}</Text></Paper>;
  if (!data) return null;

  const { merchant, top_products, categories, brands, zombies, product_groups, shopping_settings, pla_limit } = data;

  // Sort products
  const sortedProducts = [...(top_products || [])].sort((a, b) => {
    const aVal = a[sortField] || 0;
    const bVal = b[sortField] || 0;
    return sortDir === 'asc' ? aVal - bVal : bVal - aVal;
  }).filter(p => !search || (p.product_title || '').toLowerCase().includes(search.toLowerCase()) || (p.product_id || '').toLowerCase().includes(search.toLowerCase()));

  const totalCost = (top_products || []).reduce((s, p) => s + parseFloat(p.total_cost || 0), 0);
  const totalClicks = (top_products || []).reduce((s, p) => s + (p.total_clicks || 0), 0);
  const totalConversions = (top_products || []).reduce((s, p) => s + parseFloat(p.total_conversions || 0), 0);
  const uniqueProducts = (top_products || []).length;

  const SortIcon = ({ field }) => {
    if (sortField !== field) return null;
    return sortDir === 'asc' ? <CaretUp size={10} weight="bold" /> : <CaretDown size={10} weight="bold" />;
  };
  const thStyle = { cursor: 'pointer', userSelect: 'none', textAlign: 'right' };

  return (
    <Stack gap="md" px="md" py="md">
      {/* Summary stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: 8 }}>
        <StatCard label={t('shopping.stats.products')} value={uniqueProducts} sub={t('shopping.stats.productsSub')} />
        <StatCard label={t('shopping.stats.cost')} value={fmt(totalCost)} />
        <StatCard label={t('shopping.stats.clicks')} value={totalClicks.toLocaleString('pl-PL')} />
        <StatCard label={t('shopping.stats.conversions')} value={Math.round(totalConversions)} />
        {merchant && (
          <StatCard
            label={t('shopping.stats.merchantCenter')}
            value={`${merchant.approved_products || 0} / ${merchant.total_products || 0}`}
            sub={t('shopping.stats.approvedSub')}
            color={merchant.disapproved_products > 0 ? 'red' : 'green'}
          />
        )}
      </div>

      {/* Shopping settings */}
      {shopping_settings && (
        <Paper withBorder p="xs" radius="md" bg="gray.0">
          <Group gap="lg">
            <Text size="xs" c="dimmed">{t('shopping.settings.merchantId')} <Text span fw={500}>{shopping_settings.merchant_center_id || t('shopping.common.none')}</Text></Text>
            <Text size="xs" c="dimmed">{t('shopping.settings.feedLabel')} <Text span fw={500}>{shopping_settings.feed_label || t('shopping.common.none')}</Text></Text>
            <Text size="xs" c="dimmed">{t('shopping.settings.priority')} <Text span fw={500}>{t('shopping.settings.priorityLevels', { returnObjects: true })[shopping_settings.campaign_priority] || t('shopping.common.none')}</Text></Text>
          </Group>
        </Paper>
      )}

      {pla_limit?.exceeded && (
        <Paper withBorder p="sm" radius="md" bg="orange.0" style={{ borderColor: 'var(--mantine-color-orange-3)' }}>
          <Group gap="xs" wrap="nowrap">
            <WarningCircle size={18} className="text-orange-500" style={{ flexShrink: 0 }} />
            <div>
              <Text size="sm" fw={600} c="orange.8">
                {t('shopping.plaLimit.exceeded', { total: pla_limit.total_products.toLocaleString('pl-PL'), limit: pla_limit.limit.toLocaleString('pl-PL') })}
              </Text>
              <Text size="xs" c="orange.7">
                {t('shopping.plaLimit.description', { limit: pla_limit.limit.toLocaleString('pl-PL') })}
              </Text>
            </div>
          </Group>
        </Paper>
      )}

      {/* Tabs */}
      <Tabs value={tab} onChange={changeTab} variant="default" color="brand">
        <Tabs.List>
          <Tabs.Tab value="products" leftSection={<Package size={14} />}>{t('shopping.tabs.products', { count: uniqueProducts })}</Tabs.Tab>
          <Tabs.Tab value="categories" leftSection={<ShoppingCart size={14} />}>{t('shopping.tabs.categories', { count: (categories || []).length })}</Tabs.Tab>
          <Tabs.Tab value="zombies" leftSection={<Warning size={14} />}>
            {t('shopping.tabs.zombies')}
            {(zombies || []).length > 0 && <Badge size="xs" color="red" variant="filled" ml={4}>{zombies.length}</Badge>}
          </Tabs.Tab>
          <Tabs.Tab value="bids" leftSection={<CurrencyCircleDollar size={14} />} onClick={() => !bids && loadBids()}>{t('shopping.tabs.bids')}</Tabs.Tab>
          <Tabs.Tab value="ai" leftSection={<Sparkle size={14} />}>{t('shopping.tabs.ai')}</Tabs.Tab>
        </Tabs.List>

        {/* Products tab */}
        <Tabs.Panel value="products" pt="sm">
          <Group mb="xs">
            <TextInput
              placeholder={t('shopping.products.searchPlaceholder')}
              leftSection={<MagnifyingGlass size={14} />}
              size="xs"
              value={search}
              onChange={e => setSearch(e.target.value)}
              style={{ flex: 1, maxWidth: 300 }}
            />
          </Group>
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <div style={{ overflowX: 'auto', maxHeight: 500 }}>
              <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th style={{ textAlign: 'left' }}>{t('shopping.table.product')}</Table.Th>
                    <Table.Th style={thStyle} onClick={() => handleSort('total_cost')}>
                      <Group gap={2} justify="flex-end" wrap="nowrap">{t('shopping.table.cost')} <SortIcon field="total_cost" /></Group>
                    </Table.Th>
                    <Table.Th style={thStyle} onClick={() => handleSort('total_clicks')}>
                      <Group gap={2} justify="flex-end" wrap="nowrap">{t('shopping.table.clicks')} <SortIcon field="total_clicks" /></Group>
                    </Table.Th>
                    <Table.Th style={thStyle} onClick={() => handleSort('total_conversions')}>
                      <Group gap={2} justify="flex-end" wrap="nowrap">{t('shopping.table.convAbbr')} <SortIcon field="total_conversions" /></Group>
                    </Table.Th>
                    <Table.Th style={thStyle} onClick={() => handleSort('roas')}>
                      <Group gap={2} justify="flex-end" wrap="nowrap">ROAS <SortIcon field="roas" /></Group>
                    </Table.Th>
                    <Table.Th style={thStyle} onClick={() => handleSort('cpc')}>
                      <Group gap={2} justify="flex-end" wrap="nowrap">CPC <SortIcon field="cpc" /></Group>
                    </Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {sortedProducts.length === 0 && (
                    <Table.Tr>
                      <Table.Td colSpan={6} style={{ textAlign: 'center', padding: '24px 0' }}>
                        <Text size="sm" c="dimmed">{t('shopping.products.empty')}</Text>
                      </Table.Td>
                    </Table.Tr>
                  )}
                  {sortedProducts.slice(0, 100).map(p => (
                    <Table.Tr key={p.product_id}>
                      <Table.Td style={{ maxWidth: 300 }}>
                        <Text size="sm" truncate fw={500}>{p.product_title || p.product_id}</Text>
                        <Group gap={4}>
                          {p.brand && <Text size="xs" c="dimmed">{p.brand}</Text>}
                          {p.category_l1 && <Text size="xs" c="dimmed">/ {p.category_l1}</Text>}
                        </Group>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={500}>{fmt(p.total_cost)}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{(p.total_clicks || 0).toLocaleString('pl-PL')}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600}>{Math.round(parseFloat(p.total_conversions || 0))}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" fw={500} c={p.roas >= 3 ? 'green.7' : p.roas > 0 ? 'yellow.7' : 'red.6'}>
                          {p.roas > 0 ? `${p.roas}x` : '-'}
                        </Text>
                      </Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmt(p.cpc)}</Text></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </div>
          </Paper>
          {/* Full product list with server-side pagination */}
          <Button variant="light" size="xs" mt="xs" onClick={() => { loadAllProducts(1, ''); changeTab('all-products'); }}>
            {t('shopping.products.showAll')}
          </Button>
        </Tabs.Panel>

        {/* All products (server-side paginated) */}
        <Tabs.Panel value="all-products" pt="sm">
          <Group mb="xs">
            <TextInput
              placeholder={t('shopping.allProducts.searchPlaceholder')}
              leftSection={<MagnifyingGlass size={14} />}
              size="xs"
              value={serverSearch}
              onChange={e => setServerSearch(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && loadAllProducts(1, serverSearch)}
              style={{ flex: 1, maxWidth: 300 }}
            />
            <Button size="xs" variant="light" onClick={() => loadAllProducts(1, serverSearch)}>{t('shopping.common.search')}</Button>
            <Text size="xs" c="dimmed">{t('shopping.allProducts.total', { count: allProductsTotal.toLocaleString('pl-PL') })}</Text>
          </Group>
          {allProductsLoading ? <Loader color="brand" size="sm" /> : allProducts && (
            <>
              <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                <div style={{ overflowX: 'auto', maxHeight: 500 }}>
                  <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
                    <Table.Thead>
                      <Table.Tr>
                        <Table.Th>{t('shopping.table.product')}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.cost')}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.clicks')}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.convAbbr')}</Table.Th>
                        <Table.Th style={{ textAlign: 'right' }}>ROAS</Table.Th>
                      </Table.Tr>
                    </Table.Thead>
                    <Table.Tbody>
                      {allProducts.map(p => (
                        <Table.Tr key={p.product_id}>
                          <Table.Td style={{ maxWidth: 300 }}>
                            <Text size="sm" truncate fw={500}>{p.product_title || p.product_id}</Text>
                            <Group gap={4}>{p.brand && <Text size="xs" c="dimmed">{p.brand}</Text>}{p.category_l1 && <Text size="xs" c="dimmed">/ {p.category_l1}</Text>}</Group>
                          </Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={500}>{fmt(p.total_cost)}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{(p.total_clicks || 0).toLocaleString('pl-PL')}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600}>{Math.round(parseFloat(p.total_conversions || 0))}</Text></Table.Td>
                          <Table.Td style={{ textAlign: 'right' }}>
                            <Text size="sm" c={p.roas >= 3 ? 'green.7' : p.roas > 0 ? 'yellow.7' : 'red.6'}>{p.roas > 0 ? `${p.roas}x` : '-'}</Text>
                          </Table.Td>
                        </Table.Tr>
                      ))}
                    </Table.Tbody>
                  </Table>
                </div>
              </Paper>
              <Group justify="space-between" mt="xs">
                <Button size="xs" variant="default" disabled={allProductsPage <= 1} onClick={() => loadAllProducts(allProductsPage - 1, serverSearch)}>{t('shopping.pagination.previous')}</Button>
                <Text size="xs" c="dimmed">{t('shopping.pagination.pageOf', { page: allProductsPage, total: Math.ceil(allProductsTotal / 50) })}</Text>
                <Button size="xs" variant="default" disabled={allProductsPage * 50 >= allProductsTotal} onClick={() => loadAllProducts(allProductsPage + 1, serverSearch)}>{t('shopping.pagination.next')}</Button>
              </Group>
            </>
          )}
        </Tabs.Panel>

        {/* Categories tab */}
        <Tabs.Panel value="categories" pt="sm">
          <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
            <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t('shopping.categories.category')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('shopping.categories.productsCol')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.cost')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.clicks')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.conversions')}</Table.Th>
                  <Table.Th style={{ textAlign: 'right' }}>ROAS</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {(categories || []).map(c => {
                  const roas = parseFloat(c.total_cost || 0) > 0 ? (parseFloat(c.total_value || 0) / parseFloat(c.total_cost)).toFixed(2) : 0;
                  return (
                    <Table.Tr key={c.category_l1 || 'uncategorized'}>
                      <Table.Td><Text size="sm" fw={500}>{c.category_l1 || t('shopping.categories.uncategorized')}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{c.product_count}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={500}>{fmt(c.total_cost)}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{(c.total_clicks || 0).toLocaleString('pl-PL')}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600}>{Math.round(parseFloat(c.total_conversions || 0))}</Text></Table.Td>
                      <Table.Td style={{ textAlign: 'right' }}>
                        <Text size="sm" fw={500} c={roas >= 3 ? 'green.7' : roas > 0 ? 'yellow.7' : 'red.6'}>
                          {roas > 0 ? `${roas}x` : '-'}
                        </Text>
                      </Table.Td>
                    </Table.Tr>
                  );
                })}
              </Table.Tbody>
            </Table>
          </Paper>
        </Tabs.Panel>

        {/* Zombies tab */}
        <Tabs.Panel value="zombies" pt="sm">
          {(zombies || []).length === 0 ? (
            <Paper withBorder p="lg" radius="md" ta="center">
              <Text size="sm" c="dimmed">{t('shopping.zombies.empty', { cur: currencySymbol(currency) })}</Text>
            </Paper>
          ) : (
            <>
              <Paper withBorder p="sm" radius="md" mb="sm" bg="red.0" style={{ borderColor: 'var(--mantine-color-red-3)' }}>
                <Group gap="xs">
                  <WarningCircle size={16} className="text-red-500" />
                  <Text size="sm" c="red.7" fw={500}>
                    {t('shopping.zombies.warning', { count: zombies.length })}
                  </Text>
                </Group>
              </Paper>
              <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
                  <Table.Thead>
                    <Table.Tr>
                      <Table.Th>{t('shopping.table.product')}</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>{t('shopping.zombies.wastedCost')}</Table.Th>
                      <Table.Th style={{ textAlign: 'right' }}>{t('shopping.table.clicks')}</Table.Th>
                    </Table.Tr>
                  </Table.Thead>
                  <Table.Tbody>
                    {zombies.map(z => (
                      <Table.Tr key={z.product_id}>
                        <Table.Td>
                          <Text size="sm" fw={500} truncate maw={300}>{z.product_title || z.product_id}</Text>
                          {z.brand && <Text size="xs" c="dimmed">{z.brand}</Text>}
                        </Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600} c="red.6">{fmt(z.total_cost)}</Text></Table.Td>
                        <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{z.total_clicks || 0}</Text></Table.Td>
                      </Table.Tr>
                    ))}
                  </Table.Tbody>
                </Table>
              </Paper>
            </>
          )}
        </Tabs.Panel>

        {/* Bids tab */}
        <Tabs.Panel value="bids" pt="sm">
          {bidsLoading ? <Loader color="brand" size="sm" /> : bids ? (
            <Stack gap="sm">
              <Paper withBorder p="sm" radius="md" bg="gray.0">
                <Group gap="lg">
                  <Text size="xs">{t('shopping.bids.recommendations')} <Text span fw={600}>{bids.summary.total_recommendations}</Text></Text>
                  <Text size="xs" c="green.7">{t('shopping.bids.increase')} <Text span fw={600}>{bids.summary.increase}</Text></Text>
                  <Text size="xs" c="red.6">{t('shopping.bids.decrease')} <Text span fw={600}>{bids.summary.decrease}</Text></Text>
                  <Text size="xs" c="orange.6">{t('shopping.bids.zombie')} <Text span fw={600}>{bids.summary.zombie_products}</Text></Text>
                  <Text size="xs" c="green.7">{t('shopping.bids.hero')} <Text span fw={600}>{bids.summary.hero_products}</Text></Text>
                </Group>
              </Paper>

              {bidResult && (
                <Paper withBorder p="sm" radius="md" bg={bidResult.errors?.length > 0 ? 'orange.0' : 'green.0'}>
                  <Group gap="xs">
                    <CheckCircle size={16} className="text-green-600" />
                    <Text size="sm" fw={500}>{t('shopping.bids.appliedResult', { applied: bidResult.applied, total: bidResult.total })}</Text>
                  </Group>
                </Paper>
              )}

              {bids.bid_recommendations.length > 0 ? (
                <>
                  <Group justify="space-between">
                    <Text size="sm" fw={500}>{t('shopping.bids.recommendationsTitle')}</Text>
                    <Button
                      size="compact-xs" color="brand"
                      onClick={handleApplyBids} loading={applyingBids}
                      disabled={selectedBids.size === 0}
                    >
                      {t('shopping.bids.applyChanges', { count: selectedBids.size })}
                    </Button>
                  </Group>
                  <Paper withBorder radius="md" style={{ overflow: 'hidden' }}>
                    <Table highlightOnHover verticalSpacing="xs" horizontalSpacing="sm" style={{ fontSize: 13 }}>
                      <Table.Thead>
                        <Table.Tr>
                          <Table.Th style={{ width: 30 }}>
                            <Checkbox
                              size="xs"
                              checked={selectedBids.size === bids.bid_recommendations.length}
                              onChange={() => {
                                if (selectedBids.size === bids.bid_recommendations.length) {
                                  setSelectedBids(new Set());
                                } else {
                                  setSelectedBids(new Set(bids.bid_recommendations.map(r => r.product_group_id)));
                                }
                              }}
                            />
                          </Table.Th>
                          <Table.Th>{t('shopping.bids.productGroup')}</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>{t('shopping.bids.current')}</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>{t('shopping.bids.suggested')}</Table.Th>
                          <Table.Th style={{ textAlign: 'right' }}>{t('shopping.bids.change')}</Table.Th>
                          <Table.Th>{t('shopping.bids.reason')}</Table.Th>
                        </Table.Tr>
                      </Table.Thead>
                      <Table.Tbody>
                        {bids.bid_recommendations.map(r => (
                          <Table.Tr key={r.product_group_id}>
                            <Table.Td>
                              <Checkbox
                                size="xs"
                                checked={selectedBids.has(r.product_group_id)}
                                onChange={() => setSelectedBids(prev => {
                                  const next = new Set(prev);
                                  next.has(r.product_group_id) ? next.delete(r.product_group_id) : next.add(r.product_group_id);
                                  return next;
                                })}
                              />
                            </Table.Td>
                            <Table.Td>
                              <Text size="sm" fw={500}>{r.dimension_value}</Text>
                              <Text size="xs" c="dimmed">{r.dimension_type}</Text>
                            </Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}><Text size="sm">{fmt(r.current_bid)}</Text></Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}><Text size="sm" fw={600}>{fmt(r.recommended_bid)}</Text></Table.Td>
                            <Table.Td style={{ textAlign: 'right' }}>
                              <Badge size="xs" color={r.change_pct > 0 ? 'green' : 'red'}>
                                {r.change_pct > 0 ? '+' : ''}{r.change_pct}%
                              </Badge>
                            </Table.Td>
                            <Table.Td><Text size="xs" c="dimmed" maw={200} truncate>{r.reason}</Text></Table.Td>
                          </Table.Tr>
                        ))}
                      </Table.Tbody>
                    </Table>
                  </Paper>
                </>
              ) : (
                <Paper withBorder p="lg" radius="md" ta="center">
                  <Text size="sm" c="dimmed">{t('shopping.bids.noRecommendations')}</Text>
                </Paper>
              )}
            </Stack>
          ) : (
            <Paper withBorder p="lg" radius="md" ta="center">
              <CurrencyCircleDollar size={32} weight="duotone" className="text-indigo-400 mx-auto mb-2" />
              <Text size="sm" fw={500} mb={4}>{t('shopping.bids.title')}</Text>
              <Text size="xs" c="dimmed" mb="md">{t('shopping.bids.intro')}</Text>
              <Button color="brand" onClick={loadBids} loading={bidsLoading}>{t('shopping.bids.load')}</Button>
            </Paper>
          )}
        </Tabs.Panel>

        {/* AI Optimization tab */}
        <Tabs.Panel value="ai" pt="sm">
          {analyzing ? (
            <Paper withBorder p="lg" radius="md">
              <Group gap="xs" mb="sm"><Loader size="xs" /><Text size="sm" fw={500}>{t('shopping.ai.analyzing')}</Text></Group>
              <Progress
                value={analyzeProgress && analyzeProgress.total ? Math.round(analyzeProgress.current / analyzeProgress.total * 100) : 0}
                animated striped color="brand" size="lg" radius="xl"
              />
              <Text size="xs" c="dimmed" mt="xs">
                {analyzeProgress && analyzeProgress.total
                  ? t('shopping.ai.progress', { current: analyzeProgress.current, total: analyzeProgress.total, suggestions: analyzeProgress.suggestions })
                  : t('shopping.ai.starting')}
              </Text>
              <Text size="10px" c="dimmed" mt={4}>{t('shopping.ai.backgroundNote')}</Text>
            </Paper>
          ) : !analysis ? (
            <Paper withBorder p="lg" radius="md" ta="center">
              <Sparkle size={32} weight="duotone" className="text-indigo-400 mx-auto mb-2" />
              <Text size="sm" fw={500} mb={4}>{t('shopping.ai.title')}</Text>
              <Text size="xs" c="dimmed" mb="md">
                {t('shopping.ai.intro')}
              </Text>
              <Button color="brand" onClick={handleAnalyze} leftSection={<Sparkle size={14} />}>
                {t('shopping.ai.start')}
              </Button>
            </Paper>
          ) : (
            <Stack gap="sm">
              <Text size="sm" fw={500}>
                {t('shopping.ai.analyzed', { count: analysis.products_analyzed })}{analysis.truncated ? t('shopping.ai.analyzedTruncatedSuffix', { total: analysis.products_total }) : ''}
                {analysis.analyzed_at ? <Text span size="xs" c="dimmed" ml={6}>· {new Date(analysis.analyzed_at).toLocaleString('pl')}</Text> : null}
              </Text>

              {/* Feed issues */}
              {(analysis.feed_issues || []).length > 0 && (
                <Paper withBorder p="sm" radius="md">
                  <Text size="sm" fw={600} mb="xs">{t('shopping.ai.issuesTitle', { count: analysis.feed_issues.length })}</Text>
                  <Stack gap={4}>
                    {analysis.feed_issues.slice(0, 20).map((issue, i) => (
                      <Group key={i} gap="xs">
                        <Badge size="xs" color={issue.severity === 'error' ? 'red' : issue.severity === 'warning' ? 'yellow' : 'gray'}>
                          {issue.severity}
                        </Badge>
                        <Text size="xs">{issue.description}</Text>
                      </Group>
                    ))}
                  </Stack>
                </Paper>
              )}

              {/* Title suggestions */}
              {(analysis.title_suggestions || []).length > 0 && (
                <Paper withBorder p="sm" radius="md">
                  <Group justify="space-between" mb="xs" wrap="nowrap">
                    <Text size="sm" fw={600}>{t('shopping.ai.titleSuggestions', { count: analysis.title_suggestions.length })}</Text>
                    <Group gap="xs" wrap="nowrap">
                      {analysis.supplemental_feed && (
                        <Button
                          size="compact-xs" variant="light" leftSection={<Download size={12} />}
                          onClick={() => {
                            const blob = new Blob([analysis.supplemental_feed], { type: 'text/tab-separated-values' });
                            const url = URL.createObjectURL(blob);
                            const a = document.createElement('a');
                            a.href = url; a.download = 'supplemental_feed.tsv'; a.click();
                            URL.revokeObjectURL(url);
                          }}
                        >
                          {t('shopping.ai.downloadFeed')}
                        </Button>
                      )}
                      <Button size="compact-xs" variant="filled" color="brand"
                        loading={markingApplied} onClick={handleMarkTitlesApplied}>
                        {t('shopping.ai.markApplied')}
                      </Button>
                    </Group>
                  </Group>

                  {titleImpact?.has_optimization && (
                    <Paper withBorder p="xs" radius="sm" bg="brand.0" mb="xs">
                      <Text size="xs" fw={600} c="brand.7">
                        {t('shopping.ai.ctrImpact', { date: new Date(titleImpact.applied_at).toLocaleDateString('pl'), days: titleImpact.days })}
                      </Text>
                      <Group gap="lg" mt={4}>
                        <Text size="xs">{t('shopping.ai.ctrBefore')} <Text span fw={600}>{titleImpact.baseline_ctr}%</Text></Text>
                        <Text size="xs">{t('shopping.ai.ctrAfter')} <Text span fw={600}>{titleImpact.current_ctr}%</Text></Text>
                        {titleImpact.current_impressions > 0 && titleImpact.baseline_ctr > 0 && (
                          <Text size="xs" fw={600}
                            c={titleImpact.current_ctr >= titleImpact.baseline_ctr ? 'green.7' : 'red.6'}>
                            {titleImpact.current_ctr >= titleImpact.baseline_ctr ? '+' : ''}
                            {Math.round((titleImpact.current_ctr / titleImpact.baseline_ctr - 1) * 100)}%
                          </Text>
                        )}
                      </Group>
                      {titleImpact.current_impressions === 0 && (
                        <Text size="10px" c="dimmed" mt={2}>{t('shopping.ai.collectingData')}</Text>
                      )}
                    </Paper>
                  )}
                  <Stack gap="xs">
                    {analysis.title_suggestions.map((s, i) => (
                      <Paper key={i} withBorder p="xs" radius="sm" bg="gray.0">
                        <Text size="xs" c="dimmed" mb={2}>{t('shopping.ai.currentTitle')} <Text span c="dark">{s.current_title}</Text></Text>
                        <Text size="xs" c="green.7" fw={500}>{t('shopping.ai.suggestedTitle', { title: s.suggested_title })}</Text>
                        {s.issues && s.issues.length > 0 && (
                          <Group gap={4} mt={4}>
                            {s.issues.map((iss, j) => <Badge key={j} size="xs" variant="light" color="orange">{iss}</Badge>)}
                          </Group>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                </Paper>
              )}

              <Button variant="light" onClick={handleAnalyze} loading={analyzing} size="xs">
                {t('shopping.ai.repeatAnalysis')}
              </Button>

              {/* Image analysis section */}
              <Paper withBorder p="sm" radius="md" mt="md">
                <Group justify="space-between" mb="xs">
                  <Text size="sm" fw={600}>{t('shopping.ai.imageAnalysisTitle')}</Text>
                  <Button size="compact-xs" variant="light" onClick={handleImageAnalysis} loading={analyzingImages}
                    disabled={!data?.shopping_settings?.merchant_center_id}>
                    {imageAnalysis ? t('shopping.ai.repeat') : t('shopping.ai.checkImages')}
                  </Button>
                </Group>
                {imageAnalysis && (
                  <Stack gap="xs">
                    <Group gap="md">
                      <Text size="xs">{t('shopping.ai.checked')} <Text span fw={500}>{imageAnalysis.total_checked}</Text></Text>
                      {imageAnalysis.errors > 0 && <Text size="xs" c="red.6">{t('shopping.ai.errors')} <Text span fw={600}>{imageAnalysis.errors}</Text></Text>}
                      {imageAnalysis.warnings > 0 && <Text size="xs" c="yellow.7">{t('shopping.ai.warnings')} <Text span fw={600}>{imageAnalysis.warnings}</Text></Text>}
                    </Group>
                    {(imageAnalysis.image_issues || []).slice(0, 15).map((issue, i) => (
                      <Group key={i} gap="xs" wrap="nowrap">
                        <Badge size="xs" color={issue.severity === 'error' ? 'red' : issue.severity === 'warning' ? 'yellow' : 'gray'}>{issue.severity}</Badge>
                        <Text size="xs" truncate style={{ flex: 1 }}>{issue.product_title || issue.product_id}</Text>
                        <Text size="xs" c="dimmed">{issue.description}</Text>
                      </Group>
                    ))}
                  </Stack>
                )}
              </Paper>
            </Stack>
          )}
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
